import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom"; // NavLink bileşenini import ediyoruz.
import "./Sidebar.css";
import { TbAlertTriangle, TbLogout, TbUser } from "react-icons/tb";
import { usePageList } from "../../pageConfig";
import Services from "../../services/Services";
import { useAuth } from "../../AuthContext";
import { useChat } from "../../contexts/ChatContext";
import { getCustomerProfileCompletion } from "../../utils/profileCompletion";
import {
  hasAcceptedStorageOffer,
  isSecondaryContactMissing,
  secondaryContactForCompletion,
  SECONDARY_CONTACT_UPDATED_EVENT,
} from "../../utils/secondaryContact";
import { useEntityStatusSync } from "../../hooks/useEntityStatusSync";
import { getMerchantProfileCompletion } from "../../utils/merchantProfileCompletion";
import { getBillingReadiness } from "../../utils/billingReadiness";
import { oturumuKapat } from "../../utils/oturum";

const ADMIN_ROLE_DISPLAY = {
  superadmin: "Superadmin",
  admin: "Yönetici",
  "customer-relations": "Müşteri Temsilcisi",
};

const Sidebar = ({ isOpen, toggleSidebar }) => {
  const pageList = usePageList(); // ✅ context'e göre filtrelenmiş liste
  const {
    userType, logout, frontAuthRole, isCustomerConsentLocked,
    // Anasayfadaki uyarı bantlarıyla AYNI kaynaklar — sidebar rozeti bunlardan üretilir.
    documentsComplete, agreementStatus,
    // Firma kilidi: kilitliyken firma profilinde yalnız Evraklarım/Sözleşmelerim açık,
    // dolayısıyla diğer firma kalemleri tamamlanma listesine girmemeli.
    isContractLocked,
  } = useAuth();
  const chatUnread = useChat()?.totalUnread || 0;
  const navigate = useNavigate();

  const token = localStorage.getItem('token');
  const [firmData, setFirmData] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [userConsents, setUserConsents] = useState([]);
  // null = henüz yüklenmedi (puana katılmaz); dizi = get-user-documents sonucu.
  const [userDocuments, setUserDocuments] = useState(null);
  // İkincil irtibat kişisi (müşteri): undefined = henüz bilinmiyor → eksik SAYILMAZ; null = eklenmemiş.
  // Zorunluluk koşulu: kabul edilmiş DEPOLAMA teklifi var mı (undefined = henüz bilinmiyor).
  const [acceptedStorageOffer, setAcceptedStorageOffer] = useState(undefined);
  // Merchant firma tamamlanma verileri (imza/kaşe, evraklar, sözleşme, banka).
  const [companyProfileData, setCompanyProfileData] = useState({
    signature: null, documents: null, agreements: null,
    documentTypes: null, agreementTypes: null, bankAccounts: null,
  });

  const location = useLocation();
  const [openAccordions, setOpenAccordions] = useState({});

  const handleRedirectToProfilePage = () => {
    navigate("/profile");
    toggleSidebar?.();
  };

  // Eksik madde tıklaması: Profil'de ilgili sekme açılır (ProfilePage location.state okur).
  const handleRedirectToProfileTab = (tab) => {
    navigate("/profile", { state: { profileTab: tab } });
    toggleSidebar?.();
  };

  const handleLogout = () => {
    // Token/ad/soyad/userID/vade temizlenir + token sunucuda iptal edilir (utils/oturum.js).
    oturumuKapat();
    logout?.();
    toggleSidebar?.();
    navigate("/", { replace: true });
  };

  const getCompany = useCallback((companyID) => {
    if (!companyID) return;
    Services.GetWarehouseCompany(token, companyID)
      .then((res) => {
        if (res?.data?.status) {
          setFirmData(res.data.result?.[0]);
        }
      })
  }, [token]);

  const getUserInfos = useCallback(() => {
    Services.GetUserInfos(token)
      .then((res) => {
        if (res?.data?.status) {
          setUserInfo(res.data.result);
          getCompany(res.data.result.corporationID);
        }
      })
  }, [getCompany, token]);

  useEffect(() => {
    getUserInfos();
  }, [getUserInfos]);

  // Zorunlu onaylar (KVKK, Kullanıcı Sözleşmesi) tamamlanma oranına dahildir —
  // müşteride de merchant'ta da (Profil > İzinlerim her iki tipte de var).
  const getUserConsents = useCallback(() => {
    if (!["customer", "merchant"].includes(userType) || !token) return;
    Services.GetUserConsents(token, {})
      .then((res) => {
        const result = res?.data?.status ? res?.data?.result : null;
        setUserConsents(Array.isArray(result?.consents) ? result.consents : []);
      })
      .catch(() => setUserConsents([]));
  }, [token, userType]);

  useEffect(() => {
    getUserConsents();
  }, [getUserConsents]);

  // Belgeler (vergi levhası, imza sirküsü, kimlik ön/arka) da tamamlanma oranına dahildir.
  const getUserDocuments = useCallback(() => {
    if (userType !== "customer" || !token) return;
    Services.GetUserDocuments(token)
      .then((res) => {
        const result = res?.data?.status ? res?.data?.result : null;
        setUserDocuments(Array.isArray(result?.documents) ? result.documents : null);
      })
      .catch(() => setUserDocuments(null));
  }, [token, userType]);

  useEffect(() => {
    getUserDocuments();
  }, [getUserDocuments]);

  // İkincil irtibat kişileri kullanıcı bilgisinden okunur (admin'in eklediği + müşterinin eklediği
  // AYNI liste). userInfo gelene kadar undefined → "bilinmiyor", eksik sayılmaz.
  // Profil sekmesinde kaydedilince yayınlanan olayla kullanıcı bilgisi yeniden çekilir.
  const secondaryContact = useMemo(
    () => (userInfo ? (Array.isArray(userInfo.secondaryContacts) ? userInfo.secondaryContacts : []) : undefined),
    [userInfo]
  );

  useEffect(() => {
    window.addEventListener(SECONDARY_CONTACT_UPDATED_EVENT, getUserInfos);
    return () => window.removeEventListener(SECONDARY_CONTACT_UPDATED_EVENT, getUserInfos);
  }, [getUserInfos]);

  // İkincil irtibat kişisi YALNIZ depolama teklifini kabul etmiş müşteride zorunlu (29.09.2026).
  // Teklif kabul edilince (statü yayını) yeniden çekilir; sidebar sayfa değişse de açık kalır.
  const getAcceptedStorageOffer = useCallback(() => {
    if (userType !== "customer" || !token) return;
    Services.GetAllOffers(token)
      .then((res) => {
        if (!res?.data?.status) return;
        const rows = Array.isArray(res.data.result) ? res.data.result : [];
        setAcceptedStorageOffer(hasAcceptedStorageOffer(rows, localStorage.getItem("userID")));
      })
      .catch(() => {});
  }, [token, userType]);

  useEffect(() => {
    getAcceptedStorageOffer();
  }, [getAcceptedStorageOffer]);

  useEntityStatusSync("offer", getAcceptedStorageOffer);

  // Müşteride anasayfadaki "fatura + ödeme yöntemi" uyarısı için kayıtlı kartlar.
  const [paymentCards, setPaymentCards] = useState(null);
  const getPaymentCards = useCallback(() => {
    if (userType !== "customer" || !token) return;
    Services.GetPaymentLedger(token)
      .then((res) => {
        const result = res?.data?.status ? res?.data?.result : null;
        setPaymentCards(Array.isArray(result?.cards) ? result.cards : null);
      })
      .catch(() => setPaymentCards(null));
  }, [token, userType]);

  useEffect(() => {
    getPaymentCards();
  }, [getPaymentCards]);

  // Onay verildiğinde (anasayfadaki modal) rozet de anında güncellensin.
  useEffect(() => {
    const handler = () => { getUserConsents(); getPaymentCards(); };
    window.addEventListener("consents:updated", handler);
    return () => window.removeEventListener("consents:updated", handler);
  }, [getUserConsents, getPaymentCards]);

  // Merchant: firma profili tamamlanma verileri (imza/kaşe + evraklar + sözleşme + banka).
  // Tek turda çekilir; erişilemeyen kaynak null kalır ve puana KATILMAZ.
  const companyID = firmData?.companyID || userInfo?.corporationID || "";
  const getCompanyProfileData = useCallback(() => {
    if (userType !== "merchant" || !token || !companyID) return;
    Promise.allSettled([
      Services.GetCompanySignature(token, companyID),
      Services.GetWarehouseCompanyDocuments(token, companyID),
      Services.GetWarehouseCompanyAgreements(token, companyID),
      Services.GetDocumentsList(token),
      Services.GetAgreementsList(token),
      Services.GetWarehouseCompanyBankAccounts(token, companyID),
    ]).then(([sigRes, docRes, agrRes, docListRes, agrListRes, bankRes]) => {
      const ok = (res) => (res.status === "fulfilled" && res.value?.data?.status ? res.value.data.result : null);
      const asArray = (value) => (Array.isArray(value) ? value : null);
      const mandatory = (value) => value === "1" || value === 1 || value === true;

      const docList = asArray(ok(docListRes));
      const agrList = asArray(ok(agrListRes));

      setCompanyProfileData({
        // BE, imza/kaşe/yetkili adının ÜÇÜ DE boşken not_found döndürüyor (status:false).
        // Bunu null'a çevirmek "kaynak erişilemedi" ile "hiç yüklenmemiş"i karıştırıyor ve
        // İmza Yetkilisi / Firma İmzası / Firma Kaşesi maddeleri tamamlanma oranına HİÇ
        // girmiyordu. İstek TAMAMLANDIYSA boş bir nesneye düşülür (maddeler "eksik" sayılır).
        signature: ok(sigRes) || (sigRes.status === "fulfilled" ? {} : null),
        documents: asArray(ok(docRes)),
        agreements: asArray(ok(agrRes)),
        documentTypes: docList
          ? docList
            .filter((doc) => doc?.type)
            .map((doc) => ({
              type: doc.type,
              label: doc.label || doc.type,
              isMandatory: mandatory(doc?.is_mandatory ?? doc?.isMandatory),
            }))
          : null,
        agreementTypes: agrList
          ? agrList
            .filter((agreement) => agreement?.type)
            .map((agreement) => ({
              type: agreement.type,
              label: agreement.label || "İş Birliği Sözleşmesi",
            }))
          : null,
        // Banka ucu HİÇ hesap yokken {status:false} ("not_found") döner, boş dizi değil.
        // asArray(ok(...)) bunu null'a çeviriyordu; null = "veri gelmedi" sayıldığı için
        // hesabı OLMAYAN firmada "Banka Bilgileri" eksik olarak hiç görünmüyordu — yani
        // tam da eksik olduğu durumda kayboluyordu. İstek başarıyla tamamlandıysa (yanıt
        // geldiyse) sonucu boş dizi kabul ediyoruz; yalnız istek düşerse null kalır.
        bankAccounts:
          bankRes.status === "fulfilled" ? (asArray(ok(bankRes)) || []) : null,
      });
    });
  }, [token, userType, companyID]);

  useEffect(() => {
    getCompanyProfileData();
  }, [getCompanyProfileData]);

  // Profil kaydedilince (bilgi güncelleme / avatar / belge yükleme) oran anında tazelenir.
  // ProfilePage bu olayı zaten yayınlıyor; sayfa yenilemeye gerek kalmaz.
  useEffect(() => {
    const handleProfileUpdated = () => {
      getUserInfos(); getUserConsents(); getUserDocuments(); getCompanyProfileData(); getPaymentCards();
    };
    window.addEventListener("profile:updated", handleProfileUpdated);
    return () => window.removeEventListener("profile:updated", handleProfileUpdated);
  }, [getUserInfos, getUserConsents, getUserDocuments, getCompanyProfileData, getPaymentCards]);

  // Profil ekranından çıkınca tamamlanma oranı tazelenir (kullanıcı eksikleri doldurmuş olabilir).
  const previousPathRef = useRef(location.pathname);
  useEffect(() => {
    const previousPath = previousPathRef.current;
    previousPathRef.current = location.pathname;
    if (previousPath === "/profile" && location.pathname !== "/profile") {
      getUserInfos();
      getUserConsents();
      getUserDocuments();
    }
    // Merchant firma ekranından çıkınca firma tamamlanma oranı tazelenir.
    if (previousPath.startsWith("/my-warehouse-firm") && !location.pathname.startsWith("/my-warehouse-firm")) {
      getUserInfos();
      getCompanyProfileData();
    }
  }, [location.pathname, getUserInfos, getUserConsents, getUserDocuments, getCompanyProfileData]);

  // Profil tamamlanma oranı: müşteride kişisel profil, merchant'ta FİRMA profili.
  // %100'de çubuk hiç gösterilmez.
  const profileCompletion = useMemo(() => {
    if (userType === "customer") {
      return getCustomerProfileCompletion(
        userInfo, userConsents, userDocuments,
        secondaryContactForCompletion(secondaryContact, acceptedStorageOffer)
      );
    }
    if (userType === "merchant") {
      // Şirket Bilgilerim'e erişimi olmayan merchant rolü eksikleri gideremez → kutu gösterilmez.
      const canOpenFirmPage = pageList.some((section) =>
        (section.items || []).some((item) => item?.path === "/my-warehouse-firm")
      );
      return getMerchantProfileCompletion({
        userInfo,
        consents: userConsents,
        // Şirket Bilgilerim'e erişimi olmayan rolde firma maddeleri gösterilmez (giderilemez);
        // kişisel profil + onay maddeleri yine listelenir.
        ...(canOpenFirmPage ? { company: firmData, ...companyProfileData } : {}),
        // Firma kilitliyken profil/banka sekmeleri hiç çizilmiyor → o kalemler listelenmez.
        firmLocked: Boolean(isContractLocked),
      });
    }
    return null;
  }, [userType, userInfo, userConsents, userDocuments, secondaryContact, acceptedStorageOffer, firmData, companyProfileData, pageList, isContractLocked]);

  // Anasayfada bir uyarı bandı görünüyorsa sidebar'da "Anasayfa" yanında da üçgen rozet çıkar.
  // Koşullar anasayfa bileşenleriyle birebir aynıdır:
  //   - müşteri/merchant: zorunlu onay kilidi (KVKK, Kullanıcı Sözleşmesi)
  //   - müşteri: fatura bilgisi veya kayıtlı ödeme yöntemi eksik
  //   - müşteri: depolama teklifini kabul etmiş ama ikincil irtibat kişisi eklenmemiş
  //   - merchant: zorunlu evrak / iş birliği sözleşmesi tamamlanmamış
  const homeWarning = useMemo(() => {
    if (isCustomerConsentLocked) return "Onaylamanız gereken güncel metinler var";
    if (userType === "customer") {
      const readiness = getBillingReadiness({ userInfo, cards: paymentCards });
      if (readiness.known && !readiness.ready) return "Fatura ve ödeme bilgilerinizi tamamlayın";
      if (acceptedStorageOffer === true && isSecondaryContactMissing(secondaryContact)) return "İkincil irtibat kişinizi ekleyin";
      return "";
    }
    if (userType === "merchant") {
      const agreementApproved = String(agreementStatus ?? "") === "1";
      if (!documentsComplete || !agreementApproved) return "Firma bilgilerinizde tamamlanması gerekenler var";
    }
    return "";
  }, [isCustomerConsentLocked, userType, userInfo, paymentCards, secondaryContact, acceptedStorageOffer, documentsComplete, agreementStatus]);

  // Eksik maddeye tıklanınca gidilecek yer maddenin kendi "page" alanından çözülür:
  // "firm" → Şirket Bilgilerim sekmeleri, diğer her şey → Profil sekmeleri (müşteri deseni).
  const handleRedirectToCompletionTarget = (item) => {
    if (item?.page === "firm") {
      navigate("/my-warehouse-firm", { state: { initialTab: item.target } });
      toggleSidebar?.();
      return;
    }
    handleRedirectToProfileTab(item?.target);
  };

  useEffect(() => {
    // pageList ilk başta (userType yüklenmeden) farklı olabilir.
    // Index'e göre state tuttuğumuz için liste değişince yeni index'leri "açık" yapıyoruz.
    setOpenAccordions((prev) => {
      const next = { ...prev };
      for (let i = 0; i < pageList.length; i += 1) {
        if (typeof next[i] !== "boolean") next[i] = true;
      }
      return next;
    });
  }, [pageList]);

  return (
    <div className={`sidebar ${isOpen ? "open" : ""}`}>
      <div style={{ width: '100%', alignItems: 'center', justifyContent: 'center', display: 'flex', flexDirection: 'column' }}>

        <div className="user-frame">
          {userType === "merchant" ?
            <>

              {firmData ?
                <>
                  <TbUser />
                  <span>Troy Depo Partner</span>
                </>
                :
                <span>...</span>
              }
            </>
            :
            <>
              <TbUser />
              <span>{userType === "customer" ? "Müşteri" : (ADMIN_ROLE_DISPLAY[frontAuthRole] ?? "Yönetici")}</span>
            </>
          }
        </div>

        {/* Zorunlu onaylar verilmeden "Profilini Tamamla" kutusu GÖSTERİLMEZ: onay kilidindeyken
            listedeki kalemlerin hiçbirine gidilemiyor (rota kilidi hepsini anasayfaya döndürür),
            yani kullanıcıya tıklayamayacağı bir yapılacaklar listesi gösterilmiş oluyordu.
            Onaylar verilince kutu kendiliğinden geri gelir. */}
        {!isCustomerConsentLocked && profileCompletion && profileCompletion.percent < 100 ? (
          <div
            className="sidebar-profile-progress"
            title={`Eksik: ${profileCompletion.missing.map((item) => item.label).join(", ")}`}
          >
            <button
              type="button"
              className="sidebar-profile-progress-main"
              onClick={handleRedirectToProfilePage}
            >
              <div className="sidebar-profile-progress-head">
                <span>Profilini Tamamla</span>
                <span className="sidebar-profile-progress-value">%{profileCompletion.percent}</span>
              </div>
              <div className="sidebar-profile-progress-track">
                <div
                  className="sidebar-profile-progress-fill"
                  style={{ width: `${profileCompletion.percent}%` }}
                />
              </div>
            </button>
            <div className="sidebar-profile-progress-missing">
              <span className="sidebar-profile-progress-missing-title">
                Eksik bilgiler ({profileCompletion.missing.length})
              </span>
              {/* Her eksik madde, Profil'de ilgili sekmeyi açar. */}
              <ul className="sidebar-profile-progress-missing-list">
                {profileCompletion.missing.map((item) => (
                  <li key={item.label}>
                    <button
                      type="button"
                      className="sidebar-profile-progress-missing-link"
                      onClick={() => handleRedirectToCompletionTarget(item)}
                    >
                      {item.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ) : null}

        <div className="sidebar-horizontal-divider" />

      </div>


      <div className="sidebar-content">
        {pageList.map((accordion, index) => (
          <div key={index} className="sidebar-accordion">
            <div
              className="accordion-header"
            // onClick={() => toggleAccordion(index)}
            >
              {accordion.icon && (
                <span className="accordion-header-icon">{accordion.icon}</span>
              )}
              <span className="accordion-header-title">{accordion.title}</span>
              {/* {openAccordions[index] ? (
                <AiOutlineUp size={12} />
              ) : (
                <AiOutlineDown size={12} />
              )} */}
            </div>
            {openAccordions[index] && (
              <div className="accordion-content">
                {accordion.items.map((item, i) => {
                  // Zorunlu onay kilidi: menüler GİZLENMEZ, devre dışı görünür (Anasayfa hariç).
                  const consentLockedItem = isCustomerConsentLocked && item.path !== "/homepage";
                  return (
                  <NavLink
                    key={i}
                    to={item.path}
                    end={!!item.exact}
                    aria-disabled={consentLockedItem || undefined}
                    onClick={(event) => { if (consentLockedItem) event.preventDefault(); }}
                    // NavLink'in className prop'u içerisine, hem isActive hem de custom aktif kontrol ekliyoruz.
                    className={({ isActive }) => {
                      // Eğer activePaths tanımlı ise, geçerli URL'in bu yollardan biriyle başlayıp başlamadığını kontrol ediyoruz.
                      const customActive = item.activePaths
                        ? item.activePaths.some((p) =>
                          location.pathname.startsWith(p)
                        )
                        : false;
                      return "sidebar-menu-item" + ((isActive || customActive) ? " active" : "") + (consentLockedItem ? " consent-locked" : "");
                    }}
                    // Varsayılan link stillerini kaldırıyoruz.
                    style={{ textDecoration: "none", color: "inherit" }}
                  >
                    {item.icon && (
                      <span className="menu-item-icon">{item.icon}</span>
                    )}
                    <span className="menu-item-text">{item.text}</span>
                    {item.path === "/chat" && chatUnread > 0 ? (
                      <span className="menu-item-badge">{chatUnread > 99 ? "99+" : chatUnread}</span>
                    ) : null}
                    {item.path === "/homepage" && homeWarning ? (
                      <span className="menu-item-warning" title={homeWarning} aria-label={homeWarning}>
                        <TbAlertTriangle size={16} />
                      </span>
                    ) : null}
                  </NavLink>
                  );
                })}
              </div>
            )}
          </div>
        ))}

        <div className="sidebar-mobile-actions">
          <div className="sidebar-mobile-actions-divider" />
          <button
            type="button"
            className="sidebar-mobile-action-item"
            onClick={handleRedirectToProfilePage}
          >
            <span className="menu-item-icon"><TbUser strokeWidth={1} /></span>
            <span className="menu-item-text">{userType === "admin" ? "Profil" : "Profilim"}</span>
          </button>
          <button
            type="button"
            className="sidebar-mobile-action-item sidebar-mobile-action-item-danger"
            onClick={handleLogout}
          >
            <span className="menu-item-icon"><TbLogout strokeWidth={1} /></span>
            <span className="menu-item-text">Çıkış Yap</span>
          </button>
        </div>
      </div>

      {/* Uygulama sürümü — sidebar'ın en altına sabit (scroll'un dışında). package.json
          versiyonu build/start scriptinde REACT_APP_VERSION ile enjekte edilir. */}
      <div className="sidebar-version">v{process.env.REACT_APP_VERSION || "—"}</div>
    </div>
  );
};

export default Sidebar;
